import { createApp } from 'vue';
import { App } from '@/components/app';
import { Store } from 'vuex';
import type { AppInitData } from '@/types/core';
import { Router } from 'vue-router';
import { I18n } from '@/precise-price/i18n';
import { ServiceContainer } from '@/services';
import { AmplitudeService } from '@/services/amplitude-service';
import { captureException, init, setTag } from '@sentry/browser';
import { APP_CONFIG } from '@/common';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import '../styles/global.scss';

// add import { APP_CONFIG } from '@/common/config';
// to use actual config values in code
// console.log('env data', process.env.VUE_APP_BUILD_MODE, config);

export class PrecisePrice {
	private readonly rootElementId: string = 'precise-price-app';

	hashData = this.parseHash();

	// eslint-disable-next-line no-useless-constructor
	constructor(
		private store: Store<void>,
		private vueRouter: Router,
		private services: ServiceContainer,
		private i18n: I18n,
		private amplitude: AmplitudeService
	) {
		this.setupSentry();
	}

	init(): void {
		this.createApp(this.createRootElement());

		if (this.hashData && !this.store.coreModule.state.isError) {
			this.store.coreModule.actions.runApp(this.hashData);
		}
	}

	private parseHash(): AppInitData | null {
		const { location } = window;
		const { protocol, host, pathname, hash } = location;

		if (hash.match(/^#pp-next\/\w{17}\/[\w-]+\/\w+$/i)) {
			// example: #pp-next/1GCHTCEN8H1181399/customize/11
			// step will be probably used in the future
			const [, vin,, writeupId] = hash.split('/');

			return {
				vin,
				writeupId,
				vdpUrl: `${protocol}//${host}${pathname}`,
			};
		}
		return null;
	}

	private createRootElement(): HTMLDivElement {
		const rootElement: HTMLDivElement = document.createElement('div');
		const mountPoint: HTMLDivElement = document.createElement('div');

		rootElement.id = this.rootElementId;
		rootElement.className = this.rootElementId;
		rootElement.appendChild(mountPoint);
		document.body.appendChild(rootElement);

		return mountPoint;
	}

	private createApp(mountPoint: HTMLDivElement): void {
		const app = createApp(App)
			.provide('coreModule', this.store.coreModule)
			.provide('writeupModule', this.store.writeupModule)
			.provide('stepManagerModule', this.store.stepManagerModule)
			.provide('cbcModule', this.store.cbcModule)
			.provide('reserveVehicleModule', this.store.reserveVehicleModule)
			.provide('services', this.services)
			.provide('router', this.vueRouter)
			.provide('i18n', this.i18n.global)
			.use(this.i18n)
			.use(this.vueRouter)
			.use(VueReCaptcha, { siteKey: `${APP_CONFIG.RECAPTCHA_SITE_KEY}` });

		app.config.errorHandler = (error, _, info) => {
			setTag('info', info);
			captureException(error);

			console.log('vue errorHandler', error);
		};

		app.mount(mountPoint);
	}

	private setupSentry() {
		const dsnUrl = `${APP_CONFIG.SENTRY_DSN}`;

		const environment = process.env.NODE_ENV ? process.env.NODE_ENV : 'production';

		const ppVersion = process.env.VUE_APP_VERSION ? process.env.VUE_APP_VERSION : '0.1.0';

		init({
			dsn: dsnUrl,
			release: ppVersion,
			environment,
		});
	}
}
